<template>
  <div class="card-body bg-light">
    <h5 class="text-left">
      {{ formTitle }}
    </h5>

    <form class="form">
      <!-- Return Date Picker -->
      <div class="form-group text-left small">
        <label for="returnDate">Return Date</label>
        <datetime
          v-model="returnDate"
          :zone="timeZone"
          class="form-control form-control-sm"
          type="date"
          id="returnDate"
        ></datetime>
        <small class="form-text text-muted">
          Leave blank to use current time. Time should be in local timezone.
        </small>
      </div>

      <!-- Additional Charges Section -->
      <div class="form-group text-left small">
        <div v-for="(charge, index) in additionalCharges" :key="index" class="border p-2 mb-2 bg-white rounded">
          <div class="row">
            <div class="col-3">
              <MoneyField
                :key="`amount-${index}-${charge.currency}`"
                fieldName="Amount"
                v-model="charge.amount"
                :displayLabel="false"
                placeholderText="Amount"
                :currencySymbol="getCurrencySymbol(charge.currency)"
              />
            </div>
            <div class="col-3">
              <SelectField
                fieldName="Currency"
                :displayLabel="false"
                :selectOptions="currencyOptions"
                displayFieldName="label"
                keyName="value"
                v-model="charge.currency"
                placeholderText="Currency"
              />
            </div>
            <div class="col-4">
              <TextField
                fieldName="Reason"
                v-model="charge.reason"
                :displayLabel="false"
                placeholderText="Reason for charge"
              />
            </div>
            <div class="col-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-danger btn-block"
                @click="removeCharge(index)"
                title="Remove charge"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="btn btn-sm btn-outline-primary mt-2"
          @click="addCharge"
        >
          <i class="fa fa-plus mr-1"></i>
          Add Charge
        </button>
      </div>

      <!-- Validation Error Message -->
      <div v-if="validationError" class="alert alert-danger small" role="alert">
        {{ validationError }}
      </div>

      <!-- Ignore Late Return Checkbox -->
      <div class="form-group form-check text-left small">
        <input
          type="checkbox"
          class="form-check-input"
          id="ignoreLateReturn"
          v-model="ignoreLateReturn"
        />
        <label class="form-check-label" for="ignoreLateReturn">
          Ignore late return charges
        </label>
        <small class="form-text text-muted">
          Check this to waive any late return fees for this tracker.
        </small>
      </div>

      <!-- Notes Field -->
      <NotesField v-model="notes" />

      <!-- Submit Button -->
      <SubmitButton
        :buttonText="formTitle"
        :loading="loading"
        @submit="emitForm"
      />
    </form>
  </div>
</template>

<script>
import { Datetime } from "vue-datetime";
import moment from "moment-timezone";
import "vue-datetime/dist/vue-datetime.css";

import NotesField from "@/components/formFieldComponents/NotesField.vue";
import SubmitButton from "@/components/formFieldComponents/SubmitButton.vue";
import MoneyField from "@/components/formFieldComponents/MoneyField.vue";
import SelectField from "@/components/formFieldComponents/SelectField.vue";
import TextField from "@/components/formFieldComponents/TextField.vue";

export default {
  name: "ReturnToFmcForm",
  components: {
    datetime: Datetime,
    NotesField,
    SubmitButton,
    MoneyField,
    SelectField,
    TextField,
  },
  props: {
    formTitle: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    item: Object,
    itemType: String,
    displayFieldName: String,
  },
  data() {
    return {
      notes: "",
      returnDate: null,
      ignoreLateReturn: false,
      additionalCharges: [],
      currencyOptions: [
        { value: "GBP", label: "GBP (£)" },
        { value: "USD", label: "USD ($)" },
        { value: "EUR", label: "EUR (€)" },
      ],
      timeZone: "Europe/London", // Default timezone, could be made configurable
      validationError: null,
    };
  },
  computed: {
    getCurrencySymbol() {
      return (currencyObj) => {
        if (!currencyObj) return '£';
        const symbols = {
          GBP: '£',
          USD: '$',
          EUR: '€'
        };
        return symbols[currencyObj.value] || '£';
      };
    }
  },
  watch: {
    additionalCharges: {
      handler() {
        this.validationError = null;
      },
      deep: true
    }
  },
  methods: {
    addCharge() {
      this.additionalCharges.push({
        amount: null,
        currency: this.currencyOptions[0], // Default to first option (GBP object)
        reason: "",
      });
    },
    removeCharge(index) {
      this.additionalCharges.splice(index, 1);
    },
    emitForm() {
      // Clear any previous validation errors
      this.validationError = null;

      // Validate additional charges
      if (this.additionalCharges.length > 0) {
        const incompleteCharges = [];
        
        this.additionalCharges.forEach((charge, index) => {
          const issues = [];
          
          if (!charge.amount || charge.amount <= 0) {
            issues.push("amount");
          }
          if (!charge.currency || !charge.currency.value) {
            issues.push("currency");
          }
          if (!charge.reason || !charge.reason.trim()) {
            issues.push("reason");
          }
          
          if (issues.length > 0) {
            incompleteCharges.push(`Charge ${index + 1}: missing ${issues.join(", ")}`);
          }
        });
        
        if (incompleteCharges.length > 0) {
          this.validationError = `${incompleteCharges.join("; ")}`;
          return; // Prevent form submission
        }
      }

      const formData = {
        notes: this.notes,
        ignore_late_return: this.ignoreLateReturn,
      };

      // Add return date if specified (convert to Unix timestamp)
      if (this.returnDate) {
        formData.return_date = moment(this.returnDate).unix();
      }

      // Add additional charges (all are valid at this point)
      formData.additional_charges = this.additionalCharges.map((charge) => ({
        amount: Math.round(charge.amount * 100), // Convert to pence/cents
        currency: charge.currency.value, // Extract value from currency object
        reason: charge.reason.trim(),
      }));

      this.$emit("form-submission", formData);
    },
  },
};
</script>

<style scoped></style>
